/**
 * Primitivas visuais do formulário de extensão.
 *
 * Segue o padrão de `MissingDataFormulary/styled.ts` (styled-components sobre
 * MUI). O que muda em relação a ele é o empilhamento mobile das ações: o spec
 * pede o botão primário ACIMA do "Voltar" em todos os steps, então
 * `StepFooter` inverte a ordem visual no mobile em vez de só quebrar linha.
 */

import { Button as MaterialUIButton } from "@mui/material";
import styled, { css } from "styled-components";

import {
  CARD_MAX_WIDTH,
  CARD_NARROW_MAX_WIDTH,
  COLORS,
  MOBILE_MEDIA_QUERY,
} from "./tokens";

export const Background = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: ${COLORS.canvas};
  display: flex;
  align-items: flex-start;
  justify-content: center;
`;

export const Card = styled.div<{ $narrow?: boolean }>`
  width: 100%;
  max-width: ${({ $narrow }) =>
    $narrow ? CARD_NARROW_MAX_WIDTH : CARD_MAX_WIDTH};
  background-color: ${COLORS.surface};
  border-radius: 0.5rem;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
  margin: 50px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${MOBILE_MEDIA_QUERY} {
    margin: 16px;
    padding: 16px;
    gap: 16px;
  }
`;

export const Title = styled.h1`
  margin: 0;
  padding: 0;
  color: ${COLORS.ink};
  font-weight: 600;
  font-size: 34px;
  line-height: 124%;

  ${MOBILE_MEDIA_QUERY} {
    font-size: 24px;
    line-height: 133%;
  }
`;

export const Header = styled.h2`
  margin: 0;
  padding: 0;
  color: ${COLORS.ink};
  font-weight: 600;
  font-size: 24px;
  line-height: 133%;

  ${MOBILE_MEDIA_QUERY} {
    font-size: 20px;
    line-height: 140%;
  }
`;

export const Subtitle = styled.h3`
  margin: 0;
  padding: 0;
  color: ${COLORS.ink};
  font-weight: 500;
  font-size: 20px;
  line-height: 160%;

  ${MOBILE_MEDIA_QUERY} {
    font-size: 16px;
    line-height: 150%;
  }
`;

/** Rótulo de seção dentro de um card (ex.: "Dados pessoais"). */
export const SectionLabel = styled.h4`
  margin: 0;
  padding: 0;
  color: ${COLORS.ink};
  font-weight: 600;
  font-size: 16px;
  line-height: 150%;
`;

export const Paragraph = styled.p`
  margin: 0;
  padding: 0;
  color: ${COLORS.inkMuted};
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;

  ${MOBILE_MEDIA_QUERY} {
    font-size: 14px;
    line-height: 143%;
  }
`;

/** Texto auxiliar pequeno (ex.: "R$ Y/mês só titular"). */
export const Caption = styled.span`
  color: ${COLORS.inkMuted};
  font-weight: 400;
  font-size: 13px;
  line-height: 150%;
`;

/** Caixa com borda usada em cards de dependente, plano e documento. */
export const Panel = styled.div<{ $selected?: boolean }>`
  width: 100%;
  border: solid 1px
    ${({ $selected }) => ($selected ? COLORS.brand : COLORS.border)};
  border-radius: 16px;
  padding: 16px;
  background-color: ${({ $selected }) =>
    $selected ? COLORS.brandTint : COLORS.surface};
  transition: border-color 0.2s, background-color 0.2s;
`;

export const Stack = styled.div<{ $gap?: string }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ $gap = "16px" }) => $gap};
`;

export const Row = styled.div<{
  $gap?: string;
  $align?: string;
  $justify?: string;
}>`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: ${({ $gap = "16px" }) => $gap};
  align-items: ${({ $align = "center" }) => $align};
  justify-content: ${({ $justify = "flex-start" }) => $justify};
`;

/**
 * Duas colunas no web que empilham no mobile. `$keepOnMobile` mantém lado a
 * lado — o Step 2 exige isso nos quadros "Com N dependentes" / "Sem
 * dependentes".
 */
export const TwoColumns = styled.div<{
  $keepOnMobile?: boolean;
  $ratio?: string;
}>`
  width: 100%;
  display: grid;
  grid-template-columns: ${({ $ratio = "1fr 1fr" }) => $ratio};
  gap: 16px;
  align-items: start;

  ${MOBILE_MEDIA_QUERY} {
    ${({ $keepOnMobile }) =>
      $keepOnMobile
        ? css`
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          `
        : css`
            grid-template-columns: 1fr;
          `}
  }
`;

export const Divider = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid ${COLORS.border};
  margin: 0;
`;

export const Button = styled(MaterialUIButton)`
  && {
    padding: 0.25rem 1rem;
    border-radius: 0.375rem;
    height: 2.5rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    font-weight: 600;
    text-transform: none;
  }
`;

/**
 * Rodapé de ações do wizard.
 *
 * Web: "Voltar" à esquerda do primário, ambos alinhados à direita.
 * Mobile: coluna com o primário EM CIMA. `column-reverse` preserva a ordem
 * lógica no DOM (voltar antes de avançar, para leitores de tela) e inverte só a
 * apresentação, como o spec desenha.
 */
export const StepFooter = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 32px;

  ${MOBILE_MEDIA_QUERY} {
    flex-direction: column-reverse;
    gap: 12px;

    & > * {
      width: 100%;
    }
  }
`;
